/**
 * EnterpriseTalentAnalytics — Round 74 (A-C-04)
 *
 * Read-only scope-aware talent analytics built on the Round 73 foundation.
 * Surfaces operational metrics (efficiency, comeback rate, diagnostic accuracy)
 * across hierarchy scope. Descriptive, not punitive — uses neutral banding
 * language and never hides a composite formula.
 *
 * Independent from Connect, scheduling, training. Gated by
 * `canViewInScopeTalent` (Round 73 predicate).
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Loader2,
  Users,
  Building2,
  ShieldAlert,
  Gauge,
  RotateCcw,
  Stethoscope,
  TrendingUp,
  Info,
} from 'lucide-react';
import { useEnterpriseScope } from '@/hooks/useEnterpriseScope';
import { useFlooActiveScopeContext } from '@/hooks/useFlooActiveScopeContext';
import { ActiveScopeBadge } from '@/components/floo-network/ActiveScopeBadge';
import { BackToOptiNetworkButton } from '@/components/floo-network/BackToOptiNetworkButton';
import {
  useScopeTalentAnalytics,
  type ScopeTalentBreakdownRow,
} from '@/hooks/useScopeTalentAnalytics';
import type { InScopeScopeType } from '@/hooks/useInScopeTalent';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { WhyButton } from '@/components/intelligence/WhyButton';
import {
  explainTalentSummary,
  explainBand,
} from '@/lib/intelligence-explanations/builders';
import { useEffect } from 'react';
import { logIntelligenceView } from '@/lib/intelligence-explanations/audit';

const ROLE_LABELS: Record<string, string> = {
  MANAGER: 'Manager',
  OWNER: 'Owner',
  ORG_ADMIN: 'Org Admin',
  TECHNICIAN: 'Technician',
  ADVISOR: 'Service Advisor',
  STUDENT: 'Student',
};

function pct(v: number | null | undefined, digits = 0): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  return `${(v * 100).toFixed(digits)}%`;
}

function num(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return v.toLocaleString();
}

/**
 * Compute simple, transparent quartile bands from breakdown rows.
 * Bands are computed in-component so the math is fully auditable in source.
 */
function computeBands(rows: ScopeTalentBreakdownRow[]) {
  const withEff = rows.filter(
    (r) => r.avg_efficiency !== null && r.jobs_completed > 0,
  );
  const sortedEff = [...withEff].sort(
    (a, b) => (b.avg_efficiency ?? 0) - (a.avg_efficiency ?? 0),
  );

  const withCb = rows.filter(
    (r) => r.comeback_rate !== null && r.jobs_completed > 0,
  );
  const sortedCb = [...withCb].sort(
    (a, b) => (b.comeback_rate ?? 0) - (a.comeback_rate ?? 0),
  );

  const withDiag = rows.filter((r) => r.diagnostic_accuracy !== null);
  const sortedDiag = [...withDiag].sort(
    (a, b) => (b.diagnostic_accuracy ?? 0) - (a.diagnostic_accuracy ?? 0),
  );

  const q = (n: number) => Math.max(1, Math.ceil(n / 4));

  return {
    topEfficiency: sortedEff.slice(0, q(sortedEff.length)),
    midEfficiency: sortedEff.slice(q(sortedEff.length), sortedEff.length - q(sortedEff.length)),
    developmentEfficiency: sortedEff.slice(sortedEff.length - q(sortedEff.length)),
    higherComebackVariance: sortedCb.slice(0, q(sortedCb.length)),
    diagnosticStrength: sortedDiag.slice(0, q(sortedDiag.length)),
  };
}

export default function EnterpriseTalentAnalytics() {
  const navigate = useNavigate();
  const { canViewInScopeTalent, scopeLabel } = useEnterpriseScope();
  // Phase 8 — active scope when chosen; membership fallback otherwise.
  const activeCtx = useFlooActiveScopeContext();
  const scopeType = activeCtx.scopeType;
  const scopeId = activeCtx.scopeId;

  const [search, setSearch] = useState('');
  const [shopFilter, setShopFilter] = useState<string>('all');
  const [roleFilter, setRoleFilter] = useState<string>('all');

  const { summary, rows, isLoading, error } = useScopeTalentAnalytics({
    scopeType: (scopeType as InScopeScopeType) ?? null,
    scopeId: scopeId ?? null,
    // Server filters: search is server-side; shop/role applied client-side
    // for snappy UI (small row counts) — change to server-side if scope grows.
  });

  const shops = useMemo(() => {
    const set = new Map<string, string>();
    rows.forEach((r) => set.set(r.shop_id, r.shop_name));
    return Array.from(set, ([id, name]) => ({ id, name })).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [rows]);

  const roles = useMemo(() => {
    const s = new Set<string>();
    rows.forEach((r) => s.add(r.role));
    return Array.from(s).sort();
  }, [rows]);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (shopFilter !== 'all' && r.shop_id !== shopFilter) return false;
      if (roleFilter !== 'all' && r.role !== roleFilter) return false;
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        if (
          !r.display_name.toLowerCase().includes(q) &&
          !r.shop_name.toLowerCase().includes(q)
        ) {
          return false;
        }
      }
      return true;
    });
  }, [rows, shopFilter, roleFilter, search]);

  const bands = useMemo(() => computeBands(filtered), [filtered]);

  if (!canViewInScopeTalent) {
    return (
      <div className="container mx-auto p-6 max-w-2xl">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-muted-foreground" />
              <CardTitle>Access Restricted</CardTitle>
            </div>
            <CardDescription>
              In-scope talent analytics is available to managers, owners, and org admins only.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="container mx-auto p-6 max-w-6xl space-y-4">
        <BackToOptiNetworkButton testId="enterprise-talent-analytics-back-to-opti-network" />
        <div>
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-primary" />
            <h1 className="text-2xl font-semibold">{scopeLabel} Talent Analytics</h1>
            <Link
              to="/enterprise/intelligence/governance"
              className="ml-auto text-xs text-primary hover:underline"
            >
              Methodology
            </Link>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Operational metrics across your scope. Read-only, descriptive view —
            built from completed-ticket data already in the system.
          </p>
          {/* Phase 8 — active scope indicator. */}
          <ActiveScopeBadge className="mt-2" testId="floo-active-scope-badge-talent-analytics" />
        </div>

        {/* Tabs to switch between Directory and Analytics */}
        <Tabs value="analytics" className="w-full">
          <TabsList>
            <TabsTrigger value="directory" onClick={() => navigate('/enterprise/talent')}>
              Directory
            </TabsTrigger>
            <TabsTrigger value="analytics">Analytics</TabsTrigger>
            <TabsTrigger value="training" onClick={() => navigate('/enterprise/training')}>
              Training
            </TabsTrigger>
            <TabsTrigger
              value="suggestions"
              onClick={() => navigate('/enterprise/talent/suggestions')}
            >
              Suggestions
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <SummaryCard
            icon={<Users className="h-4 w-4" />}
            label="Technicians"
            value={summary ? num(summary.technician_count) : '—'}
            loading={isLoading}
          />
          <SummaryCard
            icon={<Building2 className="h-4 w-4" />}
            label="Shops in scope"
            value={summary ? num(summary.total_shop_count) : '—'}
            loading={isLoading}
          />
          <SummaryCard
            icon={<Building2 className="h-4 w-4" />}
            label="Active shops (90d)"
            hint="Shops with at least one completed ticket in the last 90 days"
            value={summary ? num(summary.active_shop_count) : '—'}
            loading={isLoading}
          />
          <SummaryCard
            icon={<Gauge className="h-4 w-4" />}
            label="Avg efficiency"
            value={summary ? pct(summary.avg_efficiency) : '—'}
            loading={isLoading}
          />
          <SummaryCard
            icon={<RotateCcw className="h-4 w-4" />}
            label="Avg comeback rate"
            value={summary ? pct(summary.avg_comeback_rate, 1) : '—'}
            loading={isLoading}
          />
          <SummaryCard
            icon={<Stethoscope className="h-4 w-4" />}
            label="Avg diagnostic accuracy"
            value={
              summary && summary.avg_diagnostic_accuracy !== null
                ? pct(summary.avg_diagnostic_accuracy)
                : 'Insufficient data'
            }
            hint={
              summary
                ? `Based on ${summary.diagnostic_sample_size} diagnostic outcome${
                    summary.diagnostic_sample_size === 1 ? '' : 's'
                  } (min 5 required)`
                : undefined
            }
            loading={isLoading}
            muted={!summary || summary.avg_diagnostic_accuracy === null}
          />
        </div>

        {/* Distribution bands */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-primary" />
              <CardTitle className="text-base">Distribution bands</CardTitle>
            </div>
            <CardDescription>
              Simple quartile groupings to help spot development opportunities and strengths.
              All raw values are shown in the table below.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <BandCard
                tone="positive"
                title="Top efficiency band"
                rows={bands.topEfficiency}
                metric={(r) => pct(r.avg_efficiency)}
              />
              <BandCard
                tone="neutral"
                title="Mid band"
                rows={bands.midEfficiency}
                metric={(r) => pct(r.avg_efficiency)}
              />
              <BandCard
                tone="warning"
                title="Development band"
                rows={bands.developmentEfficiency}
                metric={(r) => pct(r.avg_efficiency)}
                hint="Lower-efficiency quartile — opportunity for support / coaching."
              />
              <BandCard
                tone="warning"
                title="Higher comeback variance"
                rows={bands.higherComebackVariance}
                metric={(r) => pct(r.comeback_rate, 1)}
                hint="Top quartile by comeback rate."
              />
              <BandCard
                tone="positive"
                title="Diagnostic-strength group"
                rows={bands.diagnosticStrength}
                metric={(r) =>
                  `${pct(r.diagnostic_accuracy)} (n=${r.diagnostic_sample_size})`
                }
              />
            </div>
          </CardContent>
        </Card>

        {/* Filters + table */}
        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row gap-3">
              <Input
                placeholder="Search name or shop…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="sm:max-w-xs"
              />
              <Select value={shopFilter} onValueChange={setShopFilter}>
                <SelectTrigger className="sm:w-[200px]">
                  <SelectValue placeholder="All shops" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All shops</SelectItem>
                  {shops.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="sm:w-[180px]">
                  <SelectValue placeholder="All roles" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All roles</SelectItem>
                  {roles.map((r) => (
                    <SelectItem key={r} value={r}>
                      {ROLE_LABELS[r] ?? r}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : error ? (
              <p className="text-sm text-destructive py-6 text-center">
                {(error as Error).message}
              </p>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <Users className="h-8 w-8 mb-2" />
                <p className="text-sm">No matching talent in your scope.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Shop</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead className="text-right">Jobs</TableHead>
                    <TableHead className="text-right">Efficiency</TableHead>
                    <TableHead className="text-right">Comeback rate</TableHead>
                    <TableHead className="text-right">Diagnostic accuracy</TableHead>
                    <TableHead className="text-right">Member since</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((r) => (
                    <TableRow key={`${r.user_id}-${r.shop_id}`}>
                      <TableCell>
                        <Link
                          to={`/enterprise/talent/${r.user_id}?mode=in_scope`}
                          className="text-primary font-medium hover:underline"
                        >
                          {r.display_name}
                        </Link>
                      </TableCell>
                      <TableCell className="text-sm">{r.shop_name}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {ROLE_LABELS[r.role] ?? r.role}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right text-sm">{num(r.jobs_completed)}</TableCell>
                      <TableCell className="text-right text-sm">{pct(r.avg_efficiency)}</TableCell>
                      <TableCell className="text-right text-sm">
                        {pct(r.comeback_rate, 1)}
                      </TableCell>
                      <TableCell className="text-right text-sm">
                        {r.diagnostic_accuracy !== null ? (
                          <span>
                            {pct(r.diagnostic_accuracy)}{' '}
                            <span className="text-xs text-muted-foreground">
                              (n={r.diagnostic_sample_size})
                            </span>
                          </span>
                        ) : (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="text-muted-foreground inline-flex items-center gap-1">
                                — <Info className="h-3 w-3" />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              Insufficient data ({r.diagnostic_sample_size}/3 required)
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell className="text-right text-xs text-muted-foreground">
                        {r.member_since
                          ? formatDistanceToNow(new Date(r.member_since), { addSuffix: true })
                          : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </TooltipProvider>
  );
}

function SummaryCard({
  icon,
  label,
  value,
  hint,
  loading,
  muted,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint?: string;
  loading?: boolean;
  muted?: boolean;
}) {
  return (
    <Card>
      <CardContent className="p-3">
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          {icon}
          <span>{label}</span>
          {hint && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-3 w-3 ml-auto opacity-60" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">{hint}</TooltipContent>
            </Tooltip>
          )}
        </div>
        <div
          className={`mt-1 text-xl font-semibold ${muted ? 'text-muted-foreground text-base' : ''}`}
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : value}
        </div>
      </CardContent>
    </Card>
  );
}

function BandCard({
  title,
  rows,
  metric,
  tone,
  hint,
}: {
  title: string;
  rows: ScopeTalentBreakdownRow[];
  metric: (r: ScopeTalentBreakdownRow) => string;
  tone: 'positive' | 'neutral' | 'warning';
  hint?: string;
}) {
  const toneClass =
    tone === 'positive'
      ? 'border-l-4 border-l-primary'
      : tone === 'warning'
        ? 'border-l-4 border-l-amber-500'
        : 'border-l-4 border-l-muted';

  return (
    <Card className={toneClass}>
      <CardContent className="p-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">{title}</span>
          <Badge variant="secondary" className="text-xs">
            {rows.length}
          </Badge>
        </div>
        {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
        <ul className="mt-2 space-y-1 max-h-32 overflow-y-auto">
          {rows.length === 0 ? (
            <li className="text-xs text-muted-foreground">No data yet.</li>
          ) : (
            rows.map((r) => (
              <li
                key={`${r.user_id}-${r.shop_id}`}
                className="text-xs flex items-center justify-between gap-2"
              >
                <Link
                  to={`/enterprise/talent/${r.user_id}?mode=in_scope`}
                  className="text-primary hover:underline truncate"
                >
                  {r.display_name}
                </Link>
                <span className="text-muted-foreground tabular-nums shrink-0">
                  {metric(r)}
                </span>
              </li>
            ))
          )}
        </ul>
      </CardContent>
    </Card>
  );
}
